import { Subject } from 'rxjs';
import { fastEqual } from '@flowus/common/utils/tools';
import { IpcMessage } from '@flowus/shared';
import { isFlowUsApp, ipcInvoke, ipcSend, ipcOn } from '../utils/electron-ipc';
import { guessFileName } from '../common/utils/download-utils';

// 下载任务状态类型
interface DownloadTaskDetail {
  id: string;
  title: string;
  savePath: string;
  state: 'progressing' | 'completed' | 'cancelled' | 'interrupted';
  startTime: number;
  receivedBytes: number;
  totalBytes: number;
  numItems: number;
  itemIndex: number;
}

/**
 * 简化的下载管理器 - 修复RxJS兼容性问题
 */
class DownloadManager {
  private _tasks: DownloadTaskDetail[] = [];
  private _change$: Subject<void> | null = null;
  private _pollTimer?: number;
  private _allowStopSince = -Infinity;
  private _initialized = false;

  constructor() {
    // 不在构造函数中初始化RxJS - 延迟到需要时
    if (isFlowUsApp()) {
      // 使用 setTimeout 确保页面完全加载后再初始化
      setTimeout(() => this.initialize(), 0);
    } else {
      //
    }
  }

  /**
   * 延迟初始化RxJS Subject和其他组件
   */
  private initialize(): void {
    if (this._initialized) {
      return;
    }

    try {
      // 现在才创建Subject
      this._change$ = new Subject<void>();

      // 设置事件监听
      this.setupEventListeners();

      // 开始轮询
      this.startPoll();

      this._initialized = true;
    } catch (error) {
      //
    }
  }

  /**
   * 确保已初始化
   */
  private ensureInitialized(): void {
    if (!this._initialized) {
      this.initialize();
    }
  }

  /**
   * 获取tasks - 安全的getter
   */
  get tasks(): DownloadTaskDetail[] {
    return [...this._tasks];
  }

  /**
   * 获取变化观察者 - 延迟创建
   */
  get onChange() {
    this.ensureInitialized();
    if (!this._change$) {
      this._change$ = new Subject<void>();
    }
    return this._change$.asObservable();
  }

  /**
   * 设置事件监听器
   */
  private setupEventListeners(): void {
    try {
      ipcOn(IpcMessage.DownloadTasksChanged, (tasks: DownloadTaskDetail[]) => {
        if (!fastEqual(tasks, this._tasks)) {
          this._tasks = tasks || [];
          if (this._change$) {
            this._change$.next();
          }
        }
      });
    } catch (error) {
      //
    }
  }

  /**
   * 轮询下载任务（备份机制）
   */
  private startPoll(minDuration = 1000): void {
    // 非 Electron 环境直接返回
    if (!isFlowUsApp()) {
      return;
    }

    this._allowStopSince = Math.max(this._allowStopSince, Date.now() + minDuration);

    if (this._pollTimer) {
      return;
    }

    this._pollTimer = window.setInterval(async () => {
      try {
        const tasks = await ipcInvoke(IpcMessage.GetDownloadTasks);

        if (!fastEqual(tasks, this._tasks)) {
          this._tasks = tasks || [];
          if (this._change$) {
            this._change$.next();
          }
        }

        // 停止条件
        const hasActiveDownloads = this._tasks.some((t) => t.state === 'progressing');
        if (!hasActiveDownloads && Date.now() >= this._allowStopSince) {
          this.stopPoll();
        }
      } catch (error) {
        //
      }
    }, 1000);
  }

  private stopPoll(): void {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = undefined;
    }
  }

  /**
   * 开始下载
   */
  async download(opts: { id: string; url: string; fileName?: string }): Promise<void> {
    this.ensureInitialized();

    if (!isFlowUsApp()) {
      return;
    }

    try {
      // 如果没有传 fileName，尝试从 URL 中提取
      const fileName = opts.fileName || guessFileName(opts.url);

      await ipcInvoke(IpcMessage.DownloadUrl, {
        uuid: opts.id,
        url: opts.url,
        fileName,
      });

      this.startPoll();
    } catch (error) {
      // throw error;
    }
  }

  /**
   * 批量下载
   */
  async downloadMany(opts: {
    id: string;
    list: { url: string; fileName: string }[];
  }): Promise<void> {
    this.ensureInitialized();

    if (!isFlowUsApp()) {
      return;
    }

    try {
      // 对列表中的每一项也做 fileName 兜底处理
      const listWithFileName = opts.list.map((item) => ({
        ...item,
        fileName: item.fileName || guessFileName(item.url),
      }));

      await ipcInvoke(IpcMessage.DownloadMany, {
        ...opts,
        list: listWithFileName,
      });
      this.startPoll();
    } catch (error) {
      // throw error;
    }
  }

  /**
   * 取消下载
   */
  async cancel(id: string): Promise<void> {
    this.ensureInitialized();

    if (!isFlowUsApp()) {
      return;
    }

    try {
      ipcSend(IpcMessage.CancelDownload, id);
      this.startPoll();
    } catch (error) {
      //
    }
  }

  /**
   * 移除下载
   */
  async remove(id: string): Promise<void> {
    this.ensureInitialized();

    // if (!isFlowUsApp()) {
    //   return;
    // }
  }

  /**
   * 手动刷新
   */
  async refresh(): Promise<void> {
    this.ensureInitialized();

    if (!isFlowUsApp()) {
      return;
    }

    try {
      const tasks = await ipcInvoke(IpcMessage.GetDownloadTasksStatus);

      if (!fastEqual(tasks, this._tasks)) {
        this._tasks = tasks || [];
        if (this._change$) {
          this._change$.next();
        }
      }
    } catch (error) {
      //
    }
  }

  /**
   * 销毁管理器
   */
  destroy(): void {
    this.stopPoll();

    if (this._change$) {
      this._change$.complete();
      this._change$ = null;
    }

    this._initialized = false;
  }
}

// 创建单例 - 但不立即初始化
export const $downloadManager = new DownloadManager();

// 页面卸载时清理
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    $downloadManager.destroy();
  });
}
